import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, X, ChevronLeft, ChevronRight } from "lucide-react";

const TransformationsPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const transformations = [
    {
      id: 1,
      src: "/transform-4.png",
      name: "Transformatie 1",
      description: "Van overgewicht naar gespierd - geweldig resultaat",
    },
    {
      id: 2,
      src: "/transform-6.jpeg",
      name: "Transformatie 2",
      description: "Ongelooflijke verandering door toewijding en discipline",
    },
    {
      id: 3,
      src: "/transform-1.png",
      name: "Transformatie 3",
      description: "Van onfit naar fit - een indrukwekkende verandering",
    },
    {
      id: 4,
      src: "/transform-2.png",
      name: "Transformatie 4",
      description: "Gewichtsverlies en spieropbouw combinatie",
    },
    {
      id: 5,
      src: "/transform-3.png",
      name: "Transformatie 5",
      description: "Complete lifestyle verandering",
    },
    {
      id: 6,
      src: "/transform-5.png",
      name: "Transformatie 6",
      description: "Jarenlange toewijding en hard werk",
    },
  ];

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = () => {
    const newIndex = currentIndex === 0 ? transformations.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(transformations[newIndex]);
  };

  const goToNext = () => {
    const newIndex = currentIndex === transformations.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedImage(transformations[newIndex]);
  };

  return (
    <main data-testid="transformations-page" className="pt-20">
      {/* Hero Section */}
      <section className="section bg-[#0A0A0A]" data-testid="transformations-hero">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="caption mb-4 block">Echte Resultaten</span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase tracking-tighter">
              Transformaties
              <br />
              <span className="text-red-500">Van Onze Leden</span>
            </h1>
            <p className="text-zinc-400 text-lg">
              Bekijk de indrukwekkende transformaties van mensen die met Arena Gym hun
              doelen hebben bereikt. Jij kunt de volgende zijn!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Transformations Grid */}
      <section className="section bg-[#121212]" data-testid="transformations-grid">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {transformations.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group cursor-pointer relative overflow-hidden rounded-sm bg-zinc-900/50 border border-zinc-800 hover:border-red-500/30 transition-all duration-500"
                onClick={() => openLightbox(item, index)}
                data-testid={`transformation-${item.id}`}
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={item.src}
                    alt={item.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                  <div>
                    <p className="text-white font-bold text-lg font-['Barlow_Condensed'] uppercase">
                      {item.name}
                    </p>
                    <p className="text-zinc-400 text-sm">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-[#0A0A0A]" data-testid="transformations-cta">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="caption mb-4 block">Jouw Beurt</span>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase tracking-tighter">
              Start Jouw <span className="text-red-500">Transformatie</span>
            </h2>
            <p className="text-zinc-400 text-lg mb-10">
              Deze resultaten zijn bereikt met toewijding en de juiste begeleiding.
              Boek je gratis intake en ontdek wat wij voor jou kunnen betekenen.
            </p>
            <Link
              to="/boeken"
              data-testid="transformations-book-cta"
              className="btn-primary inline-flex items-center gap-2"
            >
              Boek Je Gratis Intake
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
            data-testid="lightbox"
          >
            <button
              className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors z-10"
              onClick={closeLightbox}
            >
              <X size={32} />
            </button>

            <button
              className="absolute left-4 md:left-8 text-white/70 hover:text-white transition-colors z-10 p-2"
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
            >
              <ChevronLeft size={48} />
            </button>

            <motion.div
              key={selectedImage.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="max-w-5xl max-h-[85vh] mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage.src}
                alt={selectedImage.name}
                className="max-w-full max-h-[85vh] object-contain rounded-sm"
              />
              <div className="text-center mt-4">
                <p className="text-white font-bold text-xl font-['Barlow_Condensed'] uppercase">
                  {selectedImage.name}
                </p>
                <p className="text-zinc-500 text-sm mt-1">
                  {currentIndex + 1} / {transformations.length}
                </p>
              </div>
            </motion.div>

            <button
              className="absolute right-4 md:right-8 text-white/70 hover:text-white transition-colors z-10 p-2"
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
            >
              <ChevronRight size={48} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default TransformationsPage;
